import { useState, useEffect } from "react";
import { fetchProductsByCollection } from "@/lib/shopify";
import { supabase } from "@/integrations/supabase/client";
import ShopifyProductCard from "./ShopifyProductCard";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface HomepageSection {
  id: string;
  title: string;
  collectionHandle: string;
}

interface SectionWithProducts extends HomepageSection {
  products: Array<{ node: any }>;
  isLoading: boolean;
}

const HomepageSectionComponent = ({ 
  section, 
  index 
}: { 
  section: SectionWithProducts; 
  index: number;
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const isMobile = useIsMobile();
  const { t } = useStaticTranslation("homepage");
  const displayCount = isMobile ? 4 : 8;

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>} 
      className="py-16 px-4 sm:px-[45px] lg:px-[100px]"
    >
      <div className="container mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7 }}
        >
          <div className="gold-divider mb-6" />
          <h2 className="text-4xl mb-4" style={{ fontWeight: 700 }}>{section.title}</h2>
        </motion.div>

        {section.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="gold-bar-loader" />
            <span className="text-sm text-muted-foreground">{t("loadingProducts", "טוען מוצרים...")}</span>
          </div>
        ) : section.products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t("noProducts", "לא נמצאו מוצרים בקטגוריה זו")}</p>
          </div>
        ) : (
          <>
            <div 
              className="grid gap-6 justify-center max-sm:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {section.products.slice(0, displayCount).map((product, productIndex) => (
                <motion.div
                  key={product.node.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5, delay: productIndex * 0.08 }}
                  className="w-full"
                >
                  <ShopifyProductCard product={product.node} collectionHandle={section.collectionHandle} />
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              className="flex justify-center mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Link to={`/collections/${section.collectionHandle}`}>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="gap-2 group border-primary/30 hover:border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                >
                  <span>{t("viewAllProducts", "לכל המוצרים בקטגוריה")}</span>
                  <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1 ltr:rotate-180" />
                </Button>
              </Link>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
};

const HomepageSections = () => {
  const [sections, setSections] = useState<SectionWithProducts[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSections = async () => {
      setIsLoading(true);
      try {
        // Load sections config from database
        const { data: sectionsSetting } = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'homepage_sections')
          .maybeSingle();

        if (sectionsSetting?.setting_value) {
          const value = sectionsSetting.setting_value as { sections?: HomepageSection[] };
          const configuredSections = value.sections || [];
          
          // Initialize sections with loading state
          const sectionsWithLoading: SectionWithProducts[] = configuredSections.map(s => ({
            ...s,
            products: [],
            isLoading: true
          }));
          setSections(sectionsWithLoading);
          setIsLoading(false);

          // Load products for each section
          for (let i = 0; i < configuredSections.length; i++) {
            const section = configuredSections[i];
            try {
              const result = await fetchProductsByCollection(section.collectionHandle, 10);
              setSections(prev => prev.map((s, idx) => 
                idx === i ? { ...s, products: result.edges, isLoading: false } : s
              ));
            } catch (error) {
              console.error(`Error loading products for section ${section.title}:`, error);
              setSections(prev => prev.map((s, idx) => 
                idx === i ? { ...s, isLoading: false } : s
              ));
            }
          }
        } else {
          setSections([]);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading homepage sections:', error);
        setIsLoading(false);
      }
    };

    loadSections();
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="gold-bar-loader" />
        <span className="text-sm text-muted-foreground">טוען...</span>
      </div>
    );
  }

  if (sections.length === 0) {
    return null;
  }

  return (
    <>
      {sections.map((section, index) => (
        <HomepageSectionComponent 
          key={section.id} 
          section={section} 
          index={index} 
        />
      ))}
    </>
  );
};

export default HomepageSections;
